import React, { forwardRef, useEffect, useImperativeHandle } from 'react';
import FormField from '../../../../components/common/FormField';
import { RunlifeData } from '../../../../models/CompletionModels';
import { useFormFieldHandler, useFormValidation } from '../../../../utilities/completionsUtils';

interface CompletionsRunlifeTabProps {
  runlife: RunlifeData;
  setRunlife: React.Dispatch<React.SetStateAction<RunlifeData>>;
  externalValidationErrors?: { [fieldName: string]: string };
}

const CompletionsRunlifeTab = forwardRef<any, CompletionsRunlifeTabProps>(
  ({ runlife, setRunlife, externalValidationErrors = {} }, ref) => {
    const validation = useFormValidation({ tabName: 'runlife' });
    const handleFieldChange = useFormFieldHandler(setRunlife, validation);

    const displayErrors = {
      ...validation.errors,
      ...externalValidationErrors,
    };

    useImperativeHandle(ref, () => ({
      triggerValidation: (data: RunlifeData) => {
        validation.validateAllFields(data);
      },
    }));

    // Calculate runlife days whenever startDate date changes
    useEffect(() => {
      if (runlife.startDate && runlife.startDate instanceof Date) {
        const currentDate = new Date();
        if (isNaN(runlife.startDate.getTime()) || isNaN(currentDate.getTime())) {
          setRunlife((prev) => ({ ...prev, runlifeDays: 0 }));
          return;
        }
        const timeDifference = currentDate.getTime() - runlife.startDate.getTime();
        const daysDifference = Math.floor(timeDifference / (1000 * 3600 * 24));
        setRunlife((prev) => ({ ...prev, runlifeDays: daysDifference >= 0 ? daysDifference : 0 }));
      } else {
        setRunlife((prev) => ({ ...prev, runlifeDays: 0 }));
      }
    }, [runlife.startDate, setRunlife]);

    const handleDateChange = (field: keyof RunlifeData, value: Date | null) => {
      const newData = { ...runlife, [field]: value };
      handleFieldChange(field, value);
      validation.validateAllFields(newData);
    };

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ display: 'flex', gap: 16 }}>
          <FormField
            label='Install date'
            name='installDate'
            type='date'
            value={runlife.installDate}
            required
            onChange={(_, v) => handleDateChange('installDate', v as Date | null)}
            error={displayErrors.installDate}
          />
          <FormField
            label='Start date'
            name='startDate'
            type='date'
            value={runlife.startDate}
            onChange={(_, v) => handleDateChange('startDate', v as Date | null)}
            error={displayErrors.startDate}
          />
        </div>

        <div style={{ display: 'flex', gap: 16 }}>
          <FormField
            label='Fail date'
            name='failDate'
            type='date'
            value={runlife.failDate}
            onChange={(_, v) => handleDateChange('failDate', v as Date | null)}
            error={displayErrors.failDate}
          />
          <FormField
            label='Pull date'
            name='pullDate'
            type='date'
            value={runlife.pullDate}
            onChange={(_, v) => handleDateChange('pullDate', v as Date | null)}
            error={displayErrors.pullDate}
          />
        </div>

        <div style={{ display: 'flex', gap: 16 }}>
          <FormField
            label='Teardown date'
            name='teardownDate'
            type='date'
            value={runlife.teardownDate}
            onChange={(_, v) => handleDateChange('teardownDate', v as Date | null)}
            error={displayErrors.teardownDate}
          />
          <FormField
            label='Runlife days'
            name='runlifeDays'
            type='text'
            value={runlife.runlifeDays.toString()}
            disabled
            onChange={() => undefined}
          />
        </div>
      </div>
    );
  },
);

export default CompletionsRunlifeTab;
